import {
  supportedColumnsNoneStr,
  supportedColumnTypesArray,
} from "../../data/constants/supportedColumnsNoneStr";

export const getItemNamesQl = (itemIds: number[]) => `
  query {
    items (limit: 1000, ids: ${JSON.stringify(itemIds)}, exclude_nonactive: true) {
      name
      id
      state
      board {
        state
        id
      }
      parent_item {
        id
        board{
          id
        }
      }
    }
  }
`;

export const getWorkspacesQl = () => `query {
all_workspaces:workspaces (limit: 1000) {
    id
    name
    settings {
      icon {
        color
        image
      }
    }
  }
 main_workspace:boards (workspace_ids: [null] limit:1) {
    name
    workspace{
      id
      name
      settings {
        icon {
          color
          image
        }
      }
    }
  }
}`;

export const getAccountSizeQl = () => `query {
    boards(limit: 500){
      id
    }
}`;

export const checkSubitemsQl = (itemIdArray: Number[]) => `query {
        items (ids: ${JSON.stringify(itemIdArray)}, limit: 100) {
          subitems{
            id
            }
        }
      }`;

export const checkUpdatesQl = (itemIdArray: Number[]) => `query {
        items (ids: [${itemIdArray}]) {
          updates{
            id
          }
        }
      }`;

export const getStatusColumnDataQl = (itemIdArray: Number[], columnId: string) => `query {
  items (ids: ${JSON.stringify(itemIdArray)}) {
    column_values (ids: [${JSON.stringify(columnId)}]) {
      ... on StatusValue {
        index
        value
        text
        label_style {
        color
        }
      }
    }
  }
}`;

export const getPersonColumnDataQl = (itemIdArray: number[], columnId: string) => `query {
  items (ids: ${JSON.stringify(itemIdArray)}) {
    column_values (ids: [${JSON.stringify(columnId)}]) {
      ... on PeopleValue {
        text
        persons_and_teams{
          id
          kind
        }
      }
    }
  }
}`;

export const getFileColumnDataQl = (itemIdArray: number[], columnId: string) =>
  `query {
  items (ids: ${JSON.stringify(itemIdArray)}) {
    assets(column_ids: [${JSON.stringify(columnId)}]){
        name
        file_extension
        url
        url_thumbnail
        id
      } 
      column_values(ids: [${JSON.stringify(columnId)}]){
        value
      }   
    }
}`;

export const getUsersQl = (pageSize: number, currentPage: number, searchTerm: string): string => {
  if (searchTerm !== "") {
    return `query {
    users(limit: 1000, page: ${currentPage}, name: "${searchTerm}") {
      id
      email
      name
      photo_thumb
    }
  }`;
  }

  return `query {
    users(limit: ${pageSize}, page: ${currentPage}) {
      id
      email
      name
      photo_thumb
    }
  }`;
};

export const getUsersByUserIdQl = (userIds: number[]): string =>
  `query {
    users(ids: ${JSON.stringify(userIds)}) {
      id
      email
      name
      photo_thumb
    }
  }`;

export const getTeamsByIdQl = (userIds: number[]): string =>
  `query {
  teams(ids: ${JSON.stringify(userIds)}) {
    name
    picture_url
  }
}`;

export const sendNotificationQl = (targetId: number, userId: number, text: string) =>
  `mutation {
  create_notification (user_id: ${userId}, target_id: ${targetId}, text: "${text}", target_type: Project) {
    text
  }
}`;

// mentions_list is required to actually NOTIFY a mentioned user — body mention
// markup alone only renders, it never notifies (verified against the live API).
// Monday appends its own rendered mention per list entry, so callers must send a
// body with the inline mention markup STRIPPED (see stripMentionMarkup) to avoid
// a duplicate mention. Same contract for updates and replies.
export const createUpdateQl = (): string =>
  `mutation ($itemId: ID!, $body: String!, $mentions: [UpdateMention]) {
  create_update(item_id: $itemId, body: $body, mentions_list: $mentions) {
    id
    body
    text_body
  }
}`;

export const createReplyQl = (): string =>
  `mutation ($parentId: ID!, $body: String!, $mentions: [UpdateMention]) {
  create_update(parent_id: $parentId, body: $body, mentions_list: $mentions) {
    id
    body
    text_body
  }
}`;

export const getBoardDetailsQl = (boardIds: number[]) => `query {
  boards (ids: ${JSON.stringify(boardIds)}) {
    name
    id
    state
    permissions
  }
}`;

export const getBoardColumnsQl = (boardIds: number[]) => `query {
  boards (ids: ${JSON.stringify(boardIds)}) {
    id
    name
    columns(types: ${supportedColumnsNoneStr}) {
      id
      title
      type
      settings
    }		
  }
}`;

// Every column, unfiltered, so the add-item preview can also list the types SIMB doesn't sync.
export const getBoardAllColumnsQl = (boardIds: number[]) => `query {
  boards (ids: ${JSON.stringify(boardIds)}) {
    id
    columns {
      id
      title
      type
    }
  }
}`;

export const getColumnValueByColumnIdQl = (itemIds: number[], columnId: string) => `
      query {
        items (ids: ${JSON.stringify(itemIds)}) {
          column_values (ids: "${columnId}") {
            column {
              id
              title
            }
            id
            type
            value
            text
          }
        }
      }`;

export const getColumnUpdateLogQl = (
  itemId: number,
  columnId: string,
  boardId: number,
  limit?: number,
) => `query {
  boards (ids: ${JSON.stringify(boardId)}) {
    name
    id
    activity_logs(item_ids: ${JSON.stringify(itemId)},column_ids: "${columnId}" ${limit ? `, limit: ${limit}` : ""}) {
      id
      event
      data
      created_at
    }
  }
}`;

export const getColumnUpdateLogBatchQl = (
  boardIds: number[],
  itemIds: number[],
  columnIds: string[],
) => `query {
  boards (ids: ${JSON.stringify(boardIds)}) {
    name
    id
    activity_logs(item_ids: ${JSON.stringify(itemIds)}, column_ids: ${JSON.stringify(columnIds)}, limit: 1) {
      id
      event
      data
      created_at
      user_id
    }
  }
}`;

export const batchUpdateLogQl = (items: any) => `query {
    ${items
      .map(
        (item: any) => `
      updateLog_${item.itemId}_${item.id}: boards (ids: ${JSON.stringify(parseInt(item.boardId))}) {
      name
      id
      activity_logs(item_ids: ${JSON.stringify(parseInt(item.itemId))},column_ids: "${item.id}", limit: 1){
        id
        event
        data
        created_at
      }
    }
       `,
      )
      .join("\n")}
   
  }`;

export const appSubscriptionQl = () => `query {
  app_subscription {
    billing_period
    days_left
    is_trial
    plan_id
    renewal_date
  }
}`;

export const getWebhooksByBoardQl = (boardId: number): string => `
  query {
    webhooks(board_id: ${boardId}) {
      id
      event
      board_id
      config
    }
  }
`;

export const deleteWebhookQl = (webhookId: number) => `
  mutation {
    delete_webhook (id: ${webhookId}) {
      id
    }
  }
`;

export const installWebhookQl = (webhookEvent: string, boardId: number, url: string) => `
  mutation {
    create_webhook (board_id: ${boardId}, url: "${url}", event: ${webhookEvent}) {
      id
      board_id
    }
  }`;

export const getAuthorizedBoardUsersQl = (boardId: number) => `
query {
  boards (ids: ${JSON.stringify(boardId)}) {
   owners{
      id
      name
    	email
    	photo_thumb
      account {
        id
      }
  	}
  }
}`;

export const getColumnDetailsByColumnIdQl = (boardId: number, columnIds: string[]) => `
    query {
      boards (ids: ${boardId}) {
        columns (ids: ${JSON.stringify(columnIds)}) {
          id
          title
        }		
      }
    }
`;

export const mutateAddColumnToBoardQl = (
  boardIds: number[],
  columnTitle: string,
  columnType: string,
) => `mutation{
    ${boardIds
      .map(
        (
          boardId: number,
        ) => ` col${boardId}: create_column(board_id: ${boardId}, title: "${columnTitle}", column_type: ${columnType}) {
        id
        title
        description
      }`,
      )
      .join("\n")}
    }`;

// Titles and defaults are board data the user controls, so both of these are
// parameterized rather than interpolated. Interpolating them broke the GraphQL
// document whenever a title or a status/dropdown label contained a double quote:
// JSON.stringify already escapes inner quotes, and escaping the result a second
// time turned `\"` into `\\"`, which GraphQL reads as an escaped backslash
// followed by a string terminator.
export const renameColumnQl = (): string =>
  `mutation ($boardId: ID!, $columnId: String!, $title: String!) {
  change_column_title(board_id: $boardId, column_id: $columnId, title: $title) {
    id
  }
}`;

// `defaults` is monday's JSON scalar — pass a JSON string, not an object.
export const createColumnQl = (): string =>
  `mutation ($boardId: ID!, $title: String!, $columnType: ColumnType!, $defaults: JSON) {
  create_column(board_id: $boardId, title: $title, column_type: $columnType, defaults: $defaults) {
    id
    title
    type
  }
}`;

export const getBoardItemsQl = (boardIds: number[], limit: number, columnIds?: string[] | null) => {
  const columnValuesQuery =
    columnIds && columnIds.length > 0
      ? `column_values(ids: ${JSON.stringify(columnIds)}) {
          id
          value
          text
          type
          ... on StatusValue { 
          label
          label_style {
            color
          }
          update_id
        }
        ... on PeopleValue {
          text
          persons_and_teams{
            id
            kind
          }
        }
        ... on FileValue {
          id
          text
          value
        }
          column {
            settings
            title
      }
    }`
      : `column_values {
          id
          value
          text
          type
             ... on StatusValue { 
          label
          label_style {
            color
          }
          update_id
        }
        ... on PeopleValue {
          text
          persons_and_teams{
            id
            kind
          }
        }
        ... on FileValue {
          id
          text
          value
        }
          column {
            settings
            title
          }
        }`;

  return `query {
    boards (ids: ${JSON.stringify(boardIds)}) {
      items_page(limit: ${limit}){
        items{
          id
          name
          ${columnValuesQuery}
        }
      }
    }
  }`;
};

/** Fields fetched per item on items_page / next_items_page. Inline fragments
 * surface the first StatusValue and PeopleValue without extra round-trips. */
const ITEMS_PAGE_ITEM_FIELDS = `
  id
  name
  column_values {
    id
    type
    text
    value
    column {
      title
    }
    ... on StatusValue {
      label
      label_style {
        color
      }
    }
    ... on PeopleValue {
      text
      persons_and_teams {
        id
        kind
      }
    }
  }
`;

type GetItemsPageOptions = {
  searchTerm?: string;
};

const escapeGraphQlString = (value: string): string =>
  value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');

const getItemsPageQueryParams = ({ searchTerm }: GetItemsPageOptions): string => {
  const normalizedSearchTerm = searchTerm?.trim();
  if (!normalizedSearchTerm) return "";

  return `
      query_params: {
        rules: [
          {
            column_id: "name"
            compare_value: "${escapeGraphQlString(normalizedSearchTerm)}"
            operator: contains_text
          }
        ]
      }`;
};

/**
 * First page of items for a single board using cursor-based pagination.
 * See: https://developer.monday.com/api-reference/reference/items-page
 */
export const getItemsPageQl = (
  boardId: number,
  limit: number,
  options: GetItemsPageOptions = {},
) => `query {
  boards(ids: [${boardId}]) {
    items_page(
      limit: ${limit}${getItemsPageQueryParams(options)}
    ) {
      cursor
      items { ${ITEMS_PAGE_ITEM_FIELDS} }
    }
  }
}`;

/**
 * The single most-recently-created item id on a board. Used as the deep-link
 * target for activation nudges (A3 with no selection, A1) — "bring the user to
 * the last created item on that board". Returns no items for an empty board.
 */
export const getLastCreatedItemIdQl = (boardId: number) => `query {
  boards(ids: [${boardId}]) {
    items_page(
      limit: 1
      query_params: { order_by: [{ column_id: "__creation_log__", direction: desc }] }
    ) {
      items { id }
    }
  }
}`;

/**
 * Subsequent pages via root `next_items_page`. Cursors expire 60 minutes
 * after the initial `items_page` request.
 * See: https://developer.monday.com/api-reference/reference/next-items-page
 */
export const getNextItemsPageQl = (cursor: string, limit: number) => `query {
  next_items_page(limit: ${limit}, cursor: ${JSON.stringify(cursor)}) {
    cursor
    items { ${ITEMS_PAGE_ITEM_FIELDS} }
  }
}`;

/** The column fields the item view's "Only on this board" rows are built from. */
const ITEM_COLUMN_VALUE_FIELDS = `
          id
          value
          text
          type
          ... on StatusValue { 
          label
          label_style {
            color
          }
          update_id
        }
        ... on PeopleValue {
          text
          persons_and_teams{
            id
            kind
          }
        }
        ... on FileValue {
          id
          text
          value
        }
          column {
            settings
            title
          }
       ... on DropdownValue {
        values {
          label
          id
        }
      }
      ... on BoardRelationValue {
        linked_item_ids
        linked_items {
          id
        }
      }
      ... on TagsValue {
        tag_ids
        text
      }`;

export const getItemColumnValuesQl = (itemIds: number[], columnIds?: string[]) => {
  const idsArg = columnIds?.length ? `ids: ${JSON.stringify(columnIds)}, ` : "";
  const typesArg = `types: [${supportedColumnTypesArray.join(", ")}]`;

  return `query {
  items (ids: ${JSON.stringify(itemIds)}) {
    id
    name
    board {
      id
    }
    column_values (${idsArg}${typesArg}) {
${ITEM_COLUMN_VALUE_FIELDS}
    }
  }
}`;
};

export const getAccessLevelQl = (boardIds: number[]) => `query {
  boards (ids: ${JSON.stringify(boardIds)}) {
    id
    access_level
  }
}`;

export const getTagsQl = (columnIds: string[], itemIds: number[]) => `query {
  items (ids:${JSON.stringify(itemIds)}) {
    column_values (ids:${JSON.stringify(columnIds)}) {
      ... on TagsValue {
        tag_ids
        text
      }
    }
  }
}`;

/**
 * Fetches where each item currently lives (board + group) straight from Monday.
 * Monday emits no webhook when an item is moved to a different board, so the
 * persisted boardId/boardName are the fields that go stale — item ids survive
 * a move, which makes them the only reliable key to look a location up by.
 */
export const getItemCurrentLocationsQl = (itemIds: number[]) => `query {
  items (ids: ${JSON.stringify(itemIds)}) {
    id
    board {
      id
      name
    }
    group {
      id
      title
    }
  }
}`;

export const getBoardNameQl = (boardId: number): string => `query {
  boards (ids: ${boardId}) {
    name
  }
}`;

export const getBoardHierarchyTypeQl = (boardId: number): string => `query {
  boards (ids: [${boardId}]) {
    hierarchy_type
  }
}`;

export const getDropdownLabelsQl = (itemId: number, columnId: string) => `query {
  items (ids: [${itemId}]) {
    column_values (ids: ["${columnId}"]) {
      ... on DropdownValue {
        values {
          label
          id
        }
      }
    }
  }
}`;

/**
 * One group's items, id and name only: the merge picker lists them and needs nothing else.
 * `groups(ids:)` scopes `items_page` to the group, so no group rule is needed.
 */
export const getGroupItemsPageQl = (
  boardId: number,
  groupId: string,
  limit: number,
  options: GetItemsPageOptions = {},
) => `query {
  boards(ids: [${boardId}]) {
    groups(ids: [${JSON.stringify(groupId)}]) {
      items_page(
        limit: ${limit}${getItemsPageQueryParams(options)}
      ) {
        cursor
        items { id name }
      }
    }
  }
}`;

/** The next page of `getGroupItemsPageQl`. */
export const getNextGroupItemsPageQl = (cursor: string, limit: number) => `query {
  next_items_page(limit: ${limit}, cursor: ${JSON.stringify(cursor)}) {
    cursor
    items { id name }
  }
}`;

/** What an item view editor needs before it opens: the raw value and the column's settings. */
export const getEditableColumnStateQl = `query ($itemIds: [ID!], $columnIds: [String!]) {
  items(ids: $itemIds) {
    column_values(ids: $columnIds) {
      id
      type
      text
      value
      column { settings_str }
    }
  }
}`;

/** Returns the written column in getItemColumnValuesQl's shape, so the view can show it at once. */
export const changeColumnValueQl = `mutation ($boardId: ID!, $itemId: ID!, $columnId: String!, $value: JSON!) {
  change_column_value(board_id: $boardId, item_id: $itemId, column_id: $columnId, value: $value) {
    id
    column_values(ids: [$columnId]) {
${ITEM_COLUMN_VALUE_FIELDS}
    }
  }
}`;

export const getBoardTagsQl = `query ($boardIds: [ID!]) {
  boards(ids: $boardIds) {
    tags { id name }
  }
}`;

/** Users only: teams need the teams:read scope, which the app doesn't request yet. */
export const searchPeopleQl = `query ($name: String) {
  users(limit: 50, name: $name) { id name photo_thumb_small }
}`;

/** The viewer's monday profile time zone, which monday shows dates and times in. */
export const getMyTimeZoneQl = `query {
  me { time_zone_identifier }
}`;

/** Returns the tag if the board's account already has it, else creates it. */
export const createOrGetTagQl = `mutation ($name: String!, $boardId: ID!) {
  create_or_get_tag(tag_name: $name, board_id: $boardId) { id name }
}`;

/** Names and photos for the people already in a column, which a search may not return. */
export const getUsersByIdsQl = `query ($ids: [ID!]) {
  users(ids: $ids) { id name photo_thumb_small }
}`;
